/**
 * Search index core (doc 147 §8.3 — fuse.js).
 *
 * `buildSearchRecords` flattens a manifest into searchable records (entities,
 * their fields, and Firestore paths). `SearchEngine` wraps fuse.js. Both are
 * pure / framework-free so they run identically on the main thread (tests) and
 * inside the Web Worker (`searchWorker.ts`, per PERF-04).
 */
import Fuse from 'fuse.js';
import type { AtlasManifest } from '@atlas-manifest/types';
import type { ObjectKind } from '../state/objectKinds.js';

export type SearchRecordKind = ObjectKind | 'field';

export interface SearchRecord {
  /** Stable id, e.g. `entity:Holding`, `field:Holding.symbol`, `firestore-path:/users/...`. */
  id: string;
  kind: SearchRecordKind;
  /** Primary text shown in the result row. */
  label: string;
  /** Secondary text (type, owning entity, …). */
  sublabel: string;
  /** The kind of object this record routes to via `navigateToObjectKind` — always
   *  one of the 9 catalogued `ObjectKind`s, never `'field'` (a field record's
   *  `objectKind` is `'entity'`, since it routes to its owning entity). */
  objectKind: ObjectKind;
  /** The id passed as `navigateToObjectKind`'s second argument. */
  objectId: string;
  /** Owning domain id, for the domain chip. Empty string when the kind has no domain concept (e.g. flags). */
  domain: string;
}

export interface SearchHit {
  record: SearchRecord;
  /** fuse.js score — lower is a better match. */
  score: number;
}

/** Flatten a manifest into search records: every entity, field and path. */
export function buildSearchRecords(manifest: AtlasManifest): SearchRecord[] {
  const records: SearchRecord[] = [];

  for (const entity of manifest.entities) {
    records.push({
      id: `entity:${entity.id}`,
      kind: 'entity',
      label: entity.name,
      sublabel: entity.description || `${entity.fields.length} fields`,
      objectKind: 'entity',
      objectId: entity.id,
      domain: entity.domain,
    });
    for (const field of entity.fields) {
      records.push({
        id: `field:${entity.id}.${field.name}`,
        kind: 'field',
        label: `${entity.id}.${field.name}`,
        sublabel: field.type,
        objectKind: 'entity',
        objectId: entity.id,
        domain: entity.domain,
      });
    }
  }

  const entityDomain = new Map(manifest.entities.map((e) => [e.id, e.domain]));
  for (const path of manifest.paths) {
    records.push({
      id: `firestore-path:${path.path}`,
      kind: 'firestore-path',
      label: path.path,
      sublabel: path.entityId,
      objectKind: 'firestore-path',
      objectId: path.entityId,
      domain: entityDomain.get(path.entityId) ?? '',
    });
  }

  for (const surface of manifest.surfaces) {
    records.push({
      id: `surface:${surface.id}`,
      kind: 'surface',
      label: surface.id,
      sublabel: surface.type,
      objectKind: 'surface',
      objectId: surface.id,
      domain: surface.domain,
    });
  }

  for (const fn of manifest.cloudFunctions) {
    records.push({
      id: `cloud-function:${fn.name}`,
      kind: 'cloud-function',
      label: fn.name,
      sublabel: fn.kind,
      objectKind: 'cloud-function',
      objectId: fn.name,
      domain: fn.domain,
    });
  }

  for (const event of manifest.events) {
    records.push({
      id: `event-type:${event.type}`,
      kind: 'event-type',
      label: event.type,
      sublabel: event.category,
      objectKind: 'event-type',
      objectId: event.type,
      domain: event.category,
    });
  }

  for (const hub of manifest.hubs) {
    records.push({
      id: `hub:${hub.id}`,
      kind: 'hub',
      label: hub.name,
      sublabel: hub.docRef,
      objectKind: 'hub',
      objectId: hub.id,
      domain: hub.primaryDomain ?? '',
    });
  }

  for (const feature of manifest.features ?? []) {
    records.push({
      id: `feature:${feature.id}`,
      kind: 'feature',
      label: feature.label,
      sublabel: feature.source === 'doc-section' ? 'From docs' : 'Inferred',
      objectKind: 'feature',
      objectId: feature.id,
      domain: feature.domainId,
    });
  }

  for (const row of manifest.flagCatalogRows) {
    records.push({
      id: `flag:${row.flagId}`,
      kind: 'flag',
      label: row.flagId,
      sublabel: row.description,
      objectKind: 'flag',
      objectId: row.flagId,
      domain: '',
    });
  }

  for (const machine of manifest.stateMachines) {
    records.push({
      id: `statemachine:${machine.id}`,
      kind: 'statemachine',
      label: machine.name,
      sublabel: `${machine.states.length} states`,
      objectKind: 'statemachine',
      objectId: machine.id,
      domain: machine.domain,
    });
  }

  return records;
}

const FUSE_OPTIONS: ConstructorParameters<typeof Fuse<SearchRecord>>[1] = {
  keys: [
    { name: 'label', weight: 0.7 },
    { name: 'sublabel', weight: 0.3 },
  ],
  threshold: 0.4,
  ignoreLocation: true,
  includeScore: true,
  minMatchCharLength: 2,
};

/** A fuse.js-backed search engine over a fixed set of {@link SearchRecord}s. */
export class SearchEngine {
  private readonly fuse: Fuse<SearchRecord>;
  readonly size: number;

  constructor(records: SearchRecord[]) {
    this.fuse = new Fuse(records, FUSE_OPTIONS);
    this.size = records.length;
  }

  /** Query the index. Returns up to `limit` hits, best match first. */
  search(query: string, limit = 20): SearchHit[] {
    const q = query.trim();
    if (q.length < 2) return [];
    return this.fuse
      .search(q, { limit })
      .map((r) => ({ record: r.item, score: r.score ?? 1 }));
  }
}
